import React, { useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import LogoInput from '../../Components/BenefitOfferAdmin/LogoInput';
import NewsfeedButton from '../../Components/Newsfeed/Button';
import { renderUserAvatar } from '../../Util/data';

const LearnEbookAdminModal = ({
    avatar,
    name,
    type,
    postEbook,
    posting,
    preFetchedEbook,
    sites,
    bgClass
}) => {
    const ebook = preFetchedEbook ? JSON.parse(preFetchedEbook) : null;
    const [image, setImage] = useState(null);
    const [pdfFile, setPdfFile] = useState(null);
    const [title, setTitle] = useState(null);
    const [shortDescription, setShortDescription] = useState(null);
    const [active, setActive] = useState(true);
    const unsortedSites = JSON.parse(sites || []);
    const allSites = unsortedSites.sort((a, b) => (a?.topLocation?.name || '').localeCompare(b?.topLocation?.name || ''));
    const [selectedSites, setSelectedSites] = useState([]);

    const hideModal = () => {
        const modalWrapper = document.querySelector('.modal-wrapper');
        if (modalWrapper) modalWrapper.classList.toggle('hidden');
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        postEbook(image, pdfFile, title, shortDescription, active, selectedSites);
    };

    useEffect(() => {
        if (ebook) {
            setImage(ebook?.image?.src);
            setPdfFile(ebook?.document?.src);
            setTitle(ebook?.title);
            setShortDescription(ebook?.shortDescription);
            setActive(!!ebook?.active);
            setSelectedSites([...(ebook?.sitesCodes || []).map(s => s.code)]);
        }
    }, []);

    const toggleSite = (value) => {
        let sel = selectedSites;
        if (value === '') {
            sel = selectedSites.length === allSites.length ? [] : allSites.map(s => s.siteCode);
        } else if (sel.includes(value)) {
            sel = sel.filter(v => v !== value);
        } else {
            sel = sel.concat(value);
        }
        setSelectedSites([...sel]);
    };

    return (
        <div tabIndex="-1" className="overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 w-full md:inset-0 h-full md:h-screen justify-center items-center flex bg-black/90 md:pb-12 pb-14" role="dialog">
            <div className="bg-white rounded-2xl py-6 md:w-1/2 xl:w-2/5 w-11/12 max-w-full h-auto m-auto md:mt-12 mt-14 flex flex-col gap-4">
                <div className="flex items-center justify-between px-6">
                    <h2 className="text-gray-800 font-semibold md:text-xl text-lg leading-8 mb-0">{type === 'add' ? 'Add' : 'Edit'} ebook</h2>
                    <button type="button" onClick={hideModal} className="inline-flex items-center justify-center w-6 h-6">
                        <span className="material-icons text-gray-600">close</span>
                    </button>
                </div>

                <div className="w-full py-2 md:px-6 px-4 flex items-start gap-6">
                    {avatar ? (
                        <img src={avatar} alt={name} className="w-16 h-16 shrink-0 lg:inline-flex hidden rounded-lg object-cover object-center" />
                    ) : (
                        renderUserAvatar(name, 'w-16 h-16 shrink-0 lg:inline-flex hidden', `bg-${bgClass}`)
                    )}

                    <form onSubmit={handleFormSubmit} className="flex w-full flex-col gap-2">
                        <div className="w-full">
                            <p className="font-semibold text-gray-800 text-lg mb-2">Cover image</p>
                            <LogoInput
                                handleLogoChange={(file) => setImage(file)}
                                removeImage={() => setImage(null)}
                                prefetchedLogo={image}
                            />
                        </div>

                        <label htmlFor="document" className="font-semibold text-gray-800 text-lg">
                            PDF
                            <input
                                id="document"
                                type="file"
                                accept="application/pdf"
                                className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500"
                                onChange={(e) => setPdfFile(e.target.files[0])}
                            />
                        </label>

                        <label htmlFor="title" className="font-semibold text-gray-800 text-lg">
                            Title
                            <input
                                id="title"
                                type="text"
                                className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500"
                                onChange={(e) => setTitle(e.target.value)}
                                value={title || ''}
                                required
                            />
                        </label>

                        <label htmlFor="shortDescription" className="font-semibold text-gray-800 text-lg">
                            Short description
                            <textarea
                                id="shortDescription"
                                className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500"
                                rows="2"
                                onChange={(e) => setShortDescription(e.target.value)}
                                value={shortDescription || ''}
                            />
                        </label>

                        <label htmlFor="active" className="font-semibold text-gray-800 text-lg mt-2 flex items-center gap-3">
                            <input id="active" type="checkbox" checked={!!active} onChange={(e) => setActive(e.target.checked)} />
                            Active
                        </label>

                        <label htmlFor="site" className="font-semibold text-gray-800 text-lg">
                            Enabled on sites
                            <select
                                size="20"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.currentTarget.focus();
                                    const el = e.target;
                                    const { scrollTop } = el.parentNode;
                                    setTimeout(() => el.parentNode.scrollTo(0, scrollTop), 1);
                                    toggleSite(e.target.value);
                                }}
                                onChange={(e) => e.preventDefault()}
                                multiple
                                value={selectedSites}
                                id="site"
                                className="mt-2 mb-2 w-full bg-white border border-gray-300 focus:border-blue-600 focus:outline-hidden text-gray-800 text-base leading-6 rounded p-2.5 appearance-none cursor-pointer"
                            >
                                <option value="">All Sites</option>
                                {allSites?.map((site) => (
                                    <option key={site?.siteCode} value={site?.siteCode}>
                                        {site?.topLocation?.name} - ({site?.siteCode})
                                    </option>
                                ))}
                            </select>
                        </label>

                        <div className="w-full flex items-center justify-end">
                            <NewsfeedButton classNames="md:w-auto w-full" loading={posting} title="Submit" type="primary" />
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default LearnEbookAdminModal;

LearnEbookAdminModal.propTypes = {
    avatar: PropTypes.string,
    name: PropTypes.string.isRequired,
    posting: PropTypes.bool.isRequired,
    postEbook: PropTypes.func,
    preFetchedEbook: PropTypes.string,
    type: PropTypes.oneOf(['add', 'edit']),
    sites: PropTypes.string,
    bgClass: PropTypes.string
};

LearnEbookAdminModal.defaultProps = {
    avatar: null,
    postEbook: () => {},
    preFetchedEbook: null,
    type: 'add',
    sites: null,
    bgClass: '0'
};
